/**
 * `Checkbox` (W1-12 ui-kit-i18n; contract CheckboxProps): Radix checkbox with a clickable label. 'mixed' is the
 * tri-state (aria-checked="mixed"); activating a mixed box checks it. A disabled reason keeps the box focusable
 * (aria-disabled), ignores changes and explains itself in a tooltip and through aria-describedby.
 */

import * as RC from '@radix-ui/react-checkbox';
import { useId } from 'react';
import { useTr } from './bridge';
import s from './Checkbox.module.css';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import { Tooltip } from './Tooltip';
import type { CheckboxProps } from './types';

export function Checkbox(props: CheckboxProps & { readonly className?: string | undefined }) {
  const tr = useTr();
  const id = useId();
  const reasonId = useId();
  const reason = props.disabledReason === undefined || props.disabledReason === null ? null : tr(props.disabledReason);
  const disabled = reason !== null;
  const box = (
    <RC.Root
      id={id}
      className={s.box}
      checked={props.checked === 'mixed' ? 'indeterminate' : props.checked}
      aria-disabled={disabled ? true : undefined}
      aria-describedby={disabled ? reasonId : undefined}
      onCheckedChange={(c) => {
        if (!disabled) props.onChange(c === true);
      }}
    >
      <RC.Indicator className={s.indicator}>
        <Icon name={props.checked === 'mixed' ? 'Minus' : 'Check'} size={12} />
      </RC.Indicator>
    </RC.Root>
  );
  return (
    <div className={cx(s.row, disabled && s.disabled, props.className)} data-help-id={props.helpId}>
      {reason === null ? box : <Tooltip content={reason}>{box}</Tooltip>}
      <label htmlFor={id} className={s.label}>
        {props.label}
      </label>
      {reason !== null && (
        <span id={reasonId} hidden>
          {reason}
        </span>
      )}
    </div>
  );
}
